<template>
  <ion-page>
    <div
      v-if="!navigating"
      class="px-[24px] bg-[#F6F4F0] fixed inset-0 z-[9999] min-h-screen flex flex-col"
    >
      <BackButton class="w-full mb-[16px]" :customBack="back" />
      <div class="mb-[40px]">
        <h1 class="text-[2rem] font-[900] text-[#433D31] mb-[8px] tracking-[-1px]">Forgot PIN</h1>
        <p class="text-[1rem] font-[600] text-[#433D31]">
          Enter your email address to reset your PIN.
        </p>
        <!-- Giving per login: this account's PIN is its own, and its reset goes to
             the email this account signed up with, not the main account's. -->
        <p
          v-if="isSecondaryLogin"
          class="mt-[8px] text-[0.875rem] font-[500] text-[#433D31]"
          data-test="forgot-pin-own-email"
        >
          This account has its own PIN. Use the email address you signed up with on this account.
        </p>
      </div>

      <div class="flex-1">
        <div>
          <label
            for="email-input"
            class="block text-[0.875rem] font-inter font-semibold text-[#433D31]"
          >
            Email Address
          </label>
          <input
            id="email-input"
            v-model="email"
            type="email"
            placeholder="example@email.com"
            autocomplete="email"
            class="w-full px-[16px] py-[14px] border border-[#C3C2C1] bg-[#FFFFFF] rounded-lg outline-none focus:border-[#42362B] focus:ring-2 focus:ring-[#42362B] focus:ring-offset-2 focus:ring-offset-white text-[#191817] text-[1rem] transition-all"
            :class="errorMsg ? 'border-red-500' : ''"
            :disabled="loading"
            @input="clearError"
            @keydown.enter="handleNext"
          />
          <p v-if="errorMsg" class="mt-2 text-[0.875rem] text-red-500" role="alert">
            {{ errorMsg }}
          </p>
        </div>
      </div>

      <div class="pb-[40px]">
        <button
          @click="handleNext"
          class="w-full py-[16px] rounded-full font-semibold text-[1rem] transition-all"
          :class="
            canSubmit
              ? 'bg-[#382E25] text-white active:scale-[0.98]'
              : 'bg-[#E5E3DE] text-white cursor-not-allowed'
          "
          :disabled="!canSubmit"
        >
          {{ loading ? 'Sending...' : 'Next' }}
        </button>
      </div>
    </div>
  </ion-page>
</template>

<script>
import { IonPage } from '@ionic/vue';
import BackButton from '@/components/common/BackButton.vue';
import { mapGetters } from 'vuex';
import { REGEX_EMAIL } from '@/constants/validation/regex-patterns';

export default {
  name: 'ForgotPinPage',

  components: {
    IonPage,
    BackButton,
  },

  data() {
    return {
      email: '',
      errorMsg: '',
      loading: false,
      navigating: false,
    };
  },

  computed: {
    ...mapGetters('myGiving', ['isSecondaryLogin']),

    isValidEmail() {
      return REGEX_EMAIL.test(this.email.trim());
    },

    canSubmit() {
      return this.isValidEmail && !this.loading;
    },
  },

  mounted() {
    this.prefillOwnEmail();
  },

  methods: {
    // A secondary login resets with its own email (the profile reads it for this
    // login), so offer it. The primary types it, as before.
    prefillOwnEmail() {
      const ownEmail = this.$store.state.profile?.email;
      if (this.isSecondaryLogin && !this.email && typeof ownEmail === 'string') {
        this.email = ownEmail.trim();
      }
    },

    clearError() {
      this.errorMsg = '';
    },

    back() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.replace('/my-giving');
      }
    },

    async handleNext() {
      if (!this.canSubmit) return;

      this.loading = true;
      this.clearError();

      try {
        const res = await this.$store.dispatch('myGiving/resetPinInit', {
          email: this.email.trim(),
        });

        const data = res?.data || res;

        if (data?.state === 'reset_otp') {
          this.navigating = true;
          await this.$nextTick();

          this.$router.replace({
            path: '/my-giving/pin/forgot-pin/otp',
            query: {
              email: this.email.trim(),
              maskedEmail: data.maskedEmail,
            },
          });
        } else if (data?.state === 'email_not_found') {
          this.errorMsg = 'Email address not found. Please check and try again.';
        } else {
          this.errorMsg = 'Failed to send OTP. Please try again.';
        }
      } catch (error) {
        if (error.response?.status === 404) {
          this.errorMsg = 'Email address not found. Please check and try again.';
        } else if (error.response?.status === 429) {
          this.errorMsg = 'Too many requests. Please try again later.';
        } else {
          this.errorMsg = 'Something went wrong. Please try again.';
        }
      } finally {
        this.loading = false;
        if (this.errorMsg) {
          this.navigating = false;
        }
      }
    },
  },
};
</script>
